import { useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";
import { findIndexBy, noop } from "neetocist";
import { Stepper } from "neetoui";
import PropTypes from "prop-types";

import CompletedScreen from "./CompletedScreen";
import { GENERAL_ONBOARDING_STEPS } from "./constants";
import "./onboarding.scss";
import { createSteps } from "./utils";
import WelcomeScreen from "./WelcomeScreen";

const Onboarding = ({
  currentStep = GENERAL_ONBOARDING_STEPS.WELCOME,
  setCurrentStep = noop,
  onboardingSteps = [],
  completeButtonProps = {},
  helpScreenClassName = "",
  isAnimated = true,
  disableStepClick = false,
  welcomeScreenClassName = "",
  completedScreenClassName = "",
  completedScreenSuccessMessage,
}) => {
  const steps = useMemo(
    () =>
      createSteps(
        onboardingSteps,
        setCurrentStep,
        currentStep,
        helpScreenClassName
      ),
    [onboardingSteps]
  );

  const prevStep = useRef(currentStep);

  const [direction, setDirection] = useState("");

  const getOnboardingStepIndex = stepKey =>
    steps.findIndex(step => step.key === stepKey);

  useEffect(() => {
    if (currentStep === prevStep.current) return;

    setDirection(
      getOnboardingStepIndex(currentStep) >
        getOnboardingStepIndex(prevStep.current)
        ? "forward"
        : "backward"
    );
    prevStep.current = currentStep;
  }, [currentStep]);

  if (currentStep === GENERAL_ONBOARDING_STEPS.WELCOME) {
    return (
      <WelcomeScreen
        {...{ currentStep, setCurrentStep }}
        className={welcomeScreenClassName}
        nextStep={steps[0].key}
      />
    );
  }

  if (currentStep === GENERAL_ONBOARDING_STEPS.FINISH) {
    return (
      <CompletedScreen
        {...{ completeButtonProps, completedScreenSuccessMessage }}
        className={completedScreenClassName}
      />
    );
  }

  const handleStepChange = index => setCurrentStep(steps[index].key);

  const isStepperVisible = steps.length > 1;

  return (
    <div
      className={classNames("neeto-molecules-onboarding__container", {
        "neeto-molecules-onboarding__wrapper": !isStepperVisible,
      })}
    >
      {isStepperVisible && (
        <div className="neeto-molecules-onboarding__stepper-wrapper">
          <Stepper
            {...{ steps }}
            activeIndex={findIndexBy({ key: currentStep }, steps)}
            setActiveIndex={disableStepClick ? noop : handleStepChange}
          />
        </div>
      )}
      {steps.map((step, index) => (
        <div
          key={index}
          className={classNames("neeto-molecules-onboarding__content", {
            "neeto-molecules-onboarding__content--forward overflow-x-hidden":
              isAnimated && direction === "forward",
            "neeto-molecules-onboarding__content--backward overflow-x-hidden":
              isAnimated && direction === "backward",
            hidden: currentStep !== step.key,
          })}
        >
          {step?.element}
        </div>
      ))}
    </div>
  );
};

Onboarding.propTypes = {
  /**
   * To specify the current step of the onboarding
   */
  currentStep: PropTypes.string,
  /**
   * List of Custom components for the onboarding
   */
  onboardingSteps: PropTypes.array,
  /**
   * Props for the complete button
   */
  completeButtonProps: PropTypes.object,
  /**
   * To set the step of the onboarding
   */
  setCurrentStep: PropTypes.func,
  /**
   * To pass additional classes to the help screen
   */
  helpScreenClassName: PropTypes.string,
  /**
   * A boolean value to enable or disable the animation for the stepper.
   */
  isAnimated: PropTypes.bool,
};

export default Onboarding;
